// deployment_class: customer_env_allowed
/**
 * LandingConstellation — the LIGHT + GLASS "data constellation" E3 loved: the whole
 * agency rendered as a single data-lineage web (Lakeside center → every stream →
 * every data point). It opens STATIC at the TOP LEVEL (a simple fade/appear — no
 * scripted reel, no auto-play camera, no magnify sequence · E3 2026-08-02: the play
 * animation was "too complicated"). The viewer explores the static web directly:
 *
 *   · ZOOM    — the +/− controls, the mouse wheel, and pinch
 *   · PAN     — grab + drag
 *   · INSPECT — click any node for a light-glass detail popover (what it is · its
 *               value · traces to source · verify me), anchored DIRECTLY UNDER the
 *               node and clamped fully on-screen (portaled to <body> so the modal's
 *               transform/overflow can never clip it)
 *   · CLOSE   — ✕ · Esc · click the scrim
 *
 * A persistent "Apply for an account →" CTA sits in the footer (the conversion path).
 *
 * Rendered INLINE in the landing's own React tree (never an iframe): an expand-in-
 * place glass card over the dimmed-but-visible landing. Reduced-motion keeps a plain
 * fade; controls are real buttons and nodes are focusable, so it is fully keyboard
 * operable + WCAG-AA on the light theme. (Phones get the compact LandingHint tooltip
 * instead — the full web needs desktop room; routed in LandingPreview via matchMedia.)
 *
 * ── SELECTABLE SCHEMA VIEWS (E3 2026-08-09) ──────────────────────────────────
 * The lineage web answers "where does this number come from?". Three sibling views
 * answer questions it cannot, and the header selector switches between them:
 *
 *   · Fact constellation — which measures share which conformed dimensions
 *   · Galaxy scatter     — the portfolio in a dimensional space you choose
 *   · Venn               — what needs you in more than one way
 *
 * ADDITIVE by construction: the lineage layout, highlight and render path below are
 * untouched, and every view shares this card's camera, popover, legend and CTA — so
 * switching is a change of question, not a change of instrument. Nothing regresses,
 * and the designs can be compared side by side rather than one mutated in place.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { STAGE, buildNodeIndex, layoutFocus, type FocusPlacedNode } from './landingDrillData';
import { LandingDetailPopover } from './LandingDetailPopover';
import { LandingFactConstellation } from './LandingFactConstellation';
import { LandingGalaxyScatter, type GalaxyAxisState } from './LandingGalaxyScatter';
import { LandingVennView } from './LandingVennView';
import { SCHEMA_VIEWS, stageSize, type SchemaViewId } from './landingSchemaViews';
import './landing-schema-views.css';

type CSSVars = React.CSSProperties & Record<string, string | number>;

export interface ConstellationProps {
  /** Headline of the clicked surface — shown as the view's contextual title. */
  originLabel: string;
  originSub: string;
  /** The node the clicked surface maps to. The static view opens at the top level
   *  (the whole web), but HIGHLIGHTS this node + its lineage back to the Lakeside
   *  root so the viewer sees WHERE that object lives in the web. */
  focusId: string;
  /** Where on screen the open was triggered — the card grows from there. */
  originPoint?: { xPct: number; yPct: number };
  onClose: () => void;
}

/** User-zoom bounds. */
const MIN_SCALE = 0.5;
const MAX_SCALE = 6;
/** Per-button / per-notch zoom factor. */
const ZOOM_STEP = 1.25;
/** Pixels of pointer travel before a press becomes a pan (vs a node tap). */
const DRAG_THRESHOLD = 4;

type CamView = { s: number; tx: number; ty: number };

const KIND_LABEL: Record<string, string> = {
  core: 'Root',
  hub: 'Stream',
  process: 'Rollup',
  task: 'Data point',
};

export function LandingConstellation({
  originLabel,
  originSub,
  focusId,
  originPoint,
  onClose,
}: ConstellationProps): JSX.Element {
  const index = useMemo(() => buildNodeIndex(), []);
  // The static view is always the whole web at its TOP LEVEL (rooted at the agency
  // core) — no re-rooting, no scripted dive.
  const layout = useMemo(() => layoutFocus(index, 'core'), [index]);
  const { nodes, edges } = layout;

  // STATIC lineage highlight (E3 2026-08-02): light the FULL path from the Lakeside
  // root down to the object the viewer came in from — every intermediate node + edge,
  // end to end — plus (lightly) the object's own rendered children, and recede the
  // rest of the web. Computed once on open. A whole-agency entry (focus = core) lights
  // the web evenly (no path, no dim).
  const highlight = useMemo(() => {
    const pathIds = new Set<string>();
    let cur: string | null = index.byId[focusId] ? focusId : null;
    while (cur) {
      pathIds.add(cur);
      cur = index.parentOf[cur] ?? null;
    }
    const active = pathIds.size > 1; // a genuine sub-node, not the root itself
    const branchIds = new Set<string>();
    if (active) {
      const rendered = new Set(nodes.map((n) => n.id));
      const stack = [...(index.childIdsOf[focusId] ?? [])];
      while (stack.length) {
        const id = stack.pop()!;
        if (!rendered.has(id) || branchIds.has(id)) continue;
        branchIds.add(id);
        for (const c of index.childIdsOf[id] ?? []) stack.push(c);
      }
    }
    return { pathIds, branchIds, active, match: active ? focusId : null };
  }, [index, focusId, nodes]);

  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);

  // Which schema view is on screen. Opens on the lineage web — the view the drill-in
  // was launched to answer — and the selector switches without re-opening the card.
  const [viewId, setViewId] = useState<SchemaViewId>('lineage');
  const [galaxyAxes, setGalaxyAxes] = useState<GalaxyAxisState>({ x: 'tdc', y: 'units' });
  const activeView = SCHEMA_VIEWS.find((v) => v.id === viewId) ?? SCHEMA_VIEWS[0]!;

  // Camera: user-driven scale + translate over the fit-scaled stage. Opens at the
  // identity view — the WHOLE web, fully zoomed out, IDENTICAL for every object the
  // viewer comes in from (E3 2026-08-02: no per-object auto-zoom; every entry starts
  // 100% zoomed out and the viewer zooms in manually). Only the lineage HIGHLIGHT
  // differs per object; the camera does not.
  const [view, setView] = useState<CamView>({ s: 1, tx: 0, ty: 0 });
  const [dragging, setDragging] = useState(false);
  const [nodeDetail, setNodeDetail] = useState<{
    node: FocusPlacedNode;
    rect: { top: number; left: number; width: number; height: number };
  } | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const camRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const nodeDetailRef = useRef(nodeDetail);
  nodeDetailRef.current = nodeDetail;

  // Appear delays: ring by ring (by depth), lightly staggered within a ring — a
  // one-shot draw-in (not a reel); reduced-motion drops it entirely.
  const nodeDelay = useMemo(() => {
    const perDepth = new Map<number, number>();
    const out: Record<string, number> = {};
    for (const n of nodes) {
      const c = perDepth.get(n.depth) ?? 0;
      perDepth.set(n.depth, c + 1);
      out[n.id] = n.depth * 60 + c * 10;
    }
    return out;
  }, [nodes]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    closeBtnRef.current?.focus();
    return () => cancelAnimationFrame(raf);
  }, []);

  const requestClose = useCallback((): void => {
    setClosing(true);
    window.setTimeout(onClose, 300);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        // Esc closes an open node detail first, then the whole view.
        if (nodeDetailRef.current) setNodeDetail(null);
        else requestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // ── Camera helpers (pan + zoom about a client point) ──
  const applyPan = useCallback((dxClient: number, dyClient: number): void => {
    const camEl = camRef.current;
    if (!camEl) return;
    const rect = camEl.getBoundingClientRect();
    const pxPerStage = rect.width / STAGE || 1;
    setView((v) => ({ s: v.s, tx: v.tx + dxClient / pxPerStage, ty: v.ty + dyClient / pxPerStage }));
  }, []);

  const zoomAbout = useCallback((clientX: number, clientY: number, factor: number): void => {
    const camEl = camRef.current;
    if (!camEl) return;
    const rect = camEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // Stage-space point currently under (clientX, clientY).
    const px = ((clientX - rect.left) / rect.width) * STAGE;
    const py = ((clientY - rect.top) / rect.height) * STAGE;
    setView((v) => {
      const sNew = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.s * factor));
      // Keep that stage point pinned in place while scaling.
      return { s: sNew, tx: v.tx + (v.s - sNew) * px, ty: v.ty + (v.s - sNew) * py };
    });
  }, []);

  // Switching view resets the camera to the identity framing: a pan/zoom made while
  // reading one view means nothing in the next one, and landing off-screen would read
  // as an empty view.
  const selectView = useCallback((id: SchemaViewId): void => {
    setViewId(id);
    setNodeDetail(null);
    setView({ s: 1, tx: 0, ty: 0 });
  }, []);

  // The +/− buttons zoom about the viewport centre.
  const zoomByButton = useCallback(
    (factor: number): void => {
      const vp = viewportRef.current;
      if (!vp) return;
      const r = vp.getBoundingClientRect();
      setNodeDetail(null);
      zoomAbout(r.left + r.width / 2, r.top + r.height / 2, factor);
    },
    [zoomAbout],
  );

  // ── Pointer tracking (drag-pan + pinch-zoom), attached at window level so a drag
  //    keeps working even if the pointer leaves the stage. ──
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const pinchPrevRef = useRef<number | null>(null);
  const downTargetRef = useRef<Element | null>(null);

  useEffect(() => {
    const pointers = pointersRef.current;
    const onMove = (e: PointerEvent): void => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        const start = dragStartRef.current;
        if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_THRESHOLD) {
          movedRef.current = true;
          suppressClickRef.current = true;
          if (!draggingRef.current) {
            draggingRef.current = true;
            setDragging(true);
            setNodeDetail(null); // a stale popover shouldn't float free of its node
          }
        }
        if (draggingRef.current) applyPan(e.clientX - prev.x, e.clientY - prev.y);
      } else if (pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        if (!a || !b) return;
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const prevD = pinchPrevRef.current;
        if (prevD && prevD > 0 && dist > 0) {
          suppressClickRef.current = true;
          setNodeDetail(null);
          zoomAbout((a.x + b.x) / 2, (a.y + b.y) / 2, dist / prevD);
        }
        pinchPrevRef.current = dist;
      }
    };
    const onUp = (e: PointerEvent): void => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchPrevRef.current = null;
      if (pointers.size === 0) {
        // A press that never became a drag, on empty stage → dismiss any node detail.
        if (!movedRef.current) {
          const t = downTargetRef.current;
          if (t && !t.closest('.kh-node') && !t.closest('.kh-node-pop')) setNodeDetail(null);
        }
        draggingRef.current = false;
        setDragging(false);
        dragStartRef.current = null;
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [applyPan, zoomAbout]);

  const onViewportPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Don't start a pan when pressing the detail popover.
    const t = e.target as Element;
    if (t.closest('.kh-node-pop')) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const pointers = pointersRef.current;
    downTargetRef.current = t;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      movedRef.current = false;
      suppressClickRef.current = false;
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      if (a && b) pinchPrevRef.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };

  // Wheel-to-zoom (non-passive so we can preventDefault the page scroll behind).
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      setNodeDetail(null);
      zoomAbout(e.clientX, e.clientY, e.deltaY < 0 ? 1.1 : 1 / 1.1);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [zoomAbout]);

  const onNodeClick = (n: FocusPlacedNode, e: React.MouseEvent<HTMLButtonElement>): void => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    setNodeDetail({ node: n, rect: { top: r.top, left: r.left, width: r.width, height: r.height } });
  };

  // Fit the fixed-size stage inside the card's viewport (own scroll; never the page).
  //
  // The stage BOX is per-view. The lineage web keeps its 1160 square (unchanged), but
  // the card's viewport is wide and short, so a square fits at ~0.47 — half the card's
  // width goes unused and every label renders at under half its authored size. The
  // three schema views are naturally wide, so they get a stage in the card's own
  // aspect and fit at ~0.82, which is what makes their type legible.
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const { w: stageW, h: stageH } = stageSize(viewId);
    const apply = (): void => {
      const pad = 24;
      const availW = vp.clientWidth - pad * 2;
      const availH = vp.clientHeight - pad * 2;
      const k = Math.max(0.3, Math.min(1, Math.min(availW / stageW, availH / stageH)));
      vp.style.setProperty('--k', String(k));
      vp.style.setProperty('--sw', `${stageW * k}px`);
      vp.style.setProperty('--sh', `${stageH * k}px`);
      vp.style.setProperty('--stage', `${stageW}px`);
      vp.style.setProperty('--stage-h', `${stageH}px`);
      vp.style.setProperty('--stage-shift', '0px');
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(vp);
    return () => ro.disconnect();
  }, [viewId]);

  const originVars: CSSVars = {
    '--ox': `${originPoint ? originPoint.xPct : 50}%`,
    '--oy': `${originPoint ? originPoint.yPct : 46}%`,
  };
  const camVars: CSSVars = {
    '--cam-s': view.s,
    '--cam-tx': `${view.tx}px`,
    '--cam-ty': `${view.ty}px`,
  };
  const camClass = dragging ? ' kh-cam-drag' : ' kh-cam-manual';

  return (
    <div
      className={`kh-con${entered && !closing ? ' in' : ''}${closing ? ' out' : ''}`}
      style={originVars}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className="kh-con-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Kathedral data lineage: ${originLabel}`}
      >
        <header className="kh-con-head">
          <div className="kh-con-headtext">
            <div className="kh-con-eyebrow">Lakeside Housing Authority · {activeView.label.toLowerCase()}</div>
            <div className="kh-con-title">{originLabel}</div>
            {viewId === 'lineage' ? (
              originSub && <div className="kh-con-sub">{originSub}</div>
            ) : (
              <div className="kh-sv-question">{activeView.question}</div>
            )}
          </div>
          {/* View picker. A DROPDOWN rather than a tab strip (E3 2026-08-09): more
              schema models are expected, and a strip stops fitting the header the
              moment there are more than a handful — a select stays the same size at
              any count, and carries native keyboard + mobile behaviour for free. */}
          <label className="kh-sv-picker" htmlFor="kh-sv-view-select">
            <span className="kh-sv-picker-label">View</span>
            <select
              id="kh-sv-view-select"
              className="kh-sv-select"
              value={viewId}
              onChange={(e) => selectView(e.target.value as SchemaViewId)}
              aria-label="Choose how to view the agency's data"
            >
              {SCHEMA_VIEWS.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
            <svg className="kh-sv-picker-caret" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </label>
          <div className="kh-con-controls" role="group" aria-label="Zoom and view controls">
            <button
              type="button"
              className="kh-con-ctrl"
              onClick={() => zoomByButton(1 / ZOOM_STEP)}
              aria-label="Zoom out"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              className="kh-con-ctrl"
              onClick={() => zoomByButton(ZOOM_STEP)}
              aria-label="Zoom in"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <span className="kh-con-ctrl-sep" aria-hidden="true" />
            <button
              type="button"
              ref={closeBtnRef}
              className="kh-con-ctrl kh-con-close"
              onClick={requestClose}
              aria-label="Close and return to the landing"
            >
              ✕
            </button>
          </div>
        </header>

        <div
          className={`kh-con-viewport${dragging ? ' kh-dragging' : ''}`}
          ref={viewportRef}
          onPointerDown={onViewportPointerDown}
        >
          <div className="kh-con-scaler">
            <div className="kh-con-stage">
              <div className="kh-con-level">
                <div
                  ref={camRef}
                  className={`kh-con-cam${camClass}`}
                  style={camVars}
                  id="kh-sv-panel"
                  role="group"
                  aria-label={`${activeView.label} — ${activeView.question}`}
                >
                  {viewId === 'fact' && <LandingFactConstellation index={index} />}
                  {viewId === 'galaxy' && (
                    <LandingGalaxyScatter axes={galaxyAxes} onAxesChange={setGalaxyAxes} />
                  )}
                  {viewId === 'venn' && <LandingVennView index={index} />}

                  {viewId === 'lineage' && (
                    <>
                  <svg
                    className="kh-con-edges"
                    viewBox={`0 0 ${STAGE} ${STAGE}`}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    {edges.map((e) => {
                      const onPath = highlight.pathIds.has(e.from) && highlight.pathIds.has(e.to);
                      const onBranch = highlight.branchIds.has(e.to);
                      const eLit = highlight.active && onPath;
                      const eBranch = highlight.active && !onPath && onBranch;
                      const eDim = highlight.active && !eLit && !eBranch;
                      return (
                      <line
                        key={e.id}
                        className={`kh-con-edge${eLit ? ' is-lit' : ''}${eBranch ? ' is-branch' : ''}${eDim ? ' is-dim' : ''}`}
                        x1={e.x1}
                        y1={e.y1}
                        x2={e.x2}
                        y2={e.y2}
                        style={
                          {
                            '--len': Math.hypot(e.x2 - e.x1, e.y2 - e.y1),
                            '--delay': `${nodeDelay[e.to] ?? 0}ms`,
                          } as CSSVars
                        }
                      />
                      );
                    })}
                  </svg>

                  {nodes.map((n) => {
                    const isCenter = n.id === 'core';
                    const selected = nodeDetail?.node.id === n.id;
                    const isMatch = highlight.match === n.id;
                    const isLit = highlight.active && highlight.pathIds.has(n.id) && !isMatch;
                    const isBranch = highlight.active && highlight.branchIds.has(n.id);
                    const isDim =
                      highlight.active && !highlight.pathIds.has(n.id) && !highlight.branchIds.has(n.id);
                    return (
                      <button
                        type="button"
                        key={n.id}
                        data-node-id={n.id}
                        aria-label={n.value ? `${n.label}, ${n.value}` : n.label}
                        onClick={(e) => onNodeClick(n, e)}
                        className={
                          `kh-node k-${n.kind} st-${n.status}` +
                          `${isCenter ? ' is-center' : ''}${selected ? ' is-selected' : ''}` +
                          `${isMatch ? ' is-match' : ''}${isLit ? ' is-lit' : ''}` +
                          `${isBranch ? ' is-branch' : ''}${isDim ? ' is-dim' : ''}`
                        }
                        style={
                          {
                            left: `${n.x}px`,
                            top: `${n.y}px`,
                            '--delay': `${(nodeDelay[n.id] ?? 0) + 120}ms`,
                          } as CSSVars
                        }
                      >
                        <span className="kh-node-dot" aria-hidden="true">
                          {n.kind === 'hub' ? n.icon : ''}
                        </span>
                        <span className="kh-node-lbl" aria-hidden="true">
                          {n.label}
                          {n.value && n.kind !== 'task' && <span className="kh-node-val">{n.value}</span>}
                        </span>
                      </button>
                    );
                  })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Node detail popover — click any data point to inspect it ── */}
          {nodeDetail && (
            <LandingDetailPopover
              payload={{
                kindLabel: KIND_LABEL[nodeDetail.node.kind] ?? 'Data point',
                label: nodeDetail.node.label,
                value: nodeDetail.node.value,
                detail: nodeDetail.node.detail,
                status: nodeDetail.node.status,
              }}
              anchor={nodeDetail.rect}
              onClose={() => setNodeDetail(null)}
            />
          )}
        </div>

        {/* ── Status legend + explore hint + the persistent conversion CTA ── */}
        <div className="kh-con-legend">
          <span className="lg">
            <span className="sw done" /> Completed
          </span>
          <span className="lg">
            <span className="sw motion" /> In motion
          </span>
          <span className="lg">
            <span className="sw watch" /> Needs attention
          </span>
          <span className="kh-con-explore-tip" aria-hidden="true">
            Drag to explore · scroll to zoom · click a point
          </span>
          <a className="kh-con-apply" href="/auth/login">
            Apply for an account
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
